import * as React from "react";
import { DIALOG_TRANSITION_TIME } from "../../../config";
import { hrStore } from "../../../stores/HrStore";
import { IApplicationStateEnum, IPostApplicationStateTransitionPayload, JobCategoryEnum } from "../../../types/models";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";
import { DialogHeaderPageCount, HrBotDialog } from "../../ui/Primitives";
import { KanbanTransferAcceptDialog } from "./KanbanTransferAcceptDialog";
import { KanbanTransferDeniedDialog } from "./KanbanTransferDeniedDialog";
import { KanbanTransferInterviewDialog } from "./KanbanTransferInterviewDialog";
import { KanbanTransferMessageDialog } from "./KanbanTransferMessageDialog";

type Props = {
    onSubmit: (type?: IApplicationStateEnum) => void;
    onBack?: () => void;
    type?: IApplicationStateEnum;
    firstName: string;
    fromApplicationState?: IApplicationStateEnum;
    jobCategory?: JobCategoryEnum | string;
};

export const HrKanbanDialog = ({ onSubmit, onBack, type, firstName, fromApplicationState, jobCategory }: Props) => {
    const [pageCount, setPageCount] = React.useState(1);
    const [open, setOpen] = React.useState<boolean>(!!type);
    const [headerFieldOption, setHeaderFieldOption] = React.useState<IApplicationStateEnum | undefined>(type);

    const isMobile = useMobileMediaQuery();

    const pages = type === "coding_example" ? 1 : 2;

    React.useEffect(() => {
        setOpen(!!type);
        setHeaderFieldOption(type);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);

    const handleBack = (model?: IPostApplicationStateTransitionPayload) => {
        if (pageCount === 2) {
            if (model) {
                hrStore.currentDialogForm = model;
            }
            setPageCount(1);
        } else {
            setOpen(false);
            if (onBack) {
                onBack();
            }
        }
    };

    const handleNext = (model: IPostApplicationStateTransitionPayload) => {
        hrStore.currentDialogForm = model;
        setPageCount(2);
    };

    const handleClose = () => {
        setOpen(false);
        setPageCount(1);
        // setTimeout because otherwise there's a flash of an empty dialog.
        setTimeout(() => onBack?.(), DIALOG_TRANSITION_TIME);
    };

    const handleSubmit = (model: IPostApplicationStateTransitionPayload) => {
        hrStore.currentDialogForm = model;
        setPageCount(1);
        setOpen(false);
        onSubmit(type);
    };

    return (
        <HrBotDialog open={open}>
            <div
                style={{
                    width: isMobile ? "unset" : 550,
                    display: "flex",
                    flexDirection: "column"
                }}
            >
                <DialogHeaderPageCount
                    pageCount={pageCount}
                    pages={pages}
                    onClose={handleClose}
                    status={headerFieldOption}
                />
                {pageCount < pages ? (
                    <>
                        {(type === "first_interview" || type === "second_interview") && (
                            <KanbanTransferInterviewDialog
                                onSubmit={handleNext}
                                onBack={handleBack}
                                fromApplicationState={fromApplicationState ?? "first_interview"}
                                toApplicationState={type}
                                firstName={firstName}
                            />
                        )}
                        {type === "hired" && <KanbanTransferAcceptDialog onSubmit={handleNext} onBack={handleBack} />}
                        {(type === "denied" || type === "evidence") && (
                            <KanbanTransferDeniedDialog
                                onSubmit={handleNext}
                                onBack={handleBack}
                                setHeaderField={setHeaderFieldOption}
                                firstName={firstName}
                                fromApplicationState={fromApplicationState ?? "application_received"}
                                jobCategory={jobCategory as JobCategoryEnum | undefined}
                            />
                        )}
                    </>
                ) : (
                    <KanbanTransferMessageDialog
                        onBack={handleBack}
                        onSubmit={handleSubmit}
                        cancel={pages === 1}
                        firstName={firstName}
                        allowScheduledEmail={type === "denied"}
                    />
                )}
            </div>
        </HrBotDialog>
    );
};
